// ============================================================
// thumbScene - a map's establishing shot as flat polygons, for a library
// card. Pure: the same steps the scene renderer takes (the map's solids,
// a camera fitted to its boundary, the faces sorted far to near, each
// coloured as the scene colours it), at card size. A scene adds a dot for
// each person placed on it; a character is the figure its plate draws.
// Drawing them is utils/thumbCanvas; nothing here touches a canvas or the DOM.
// ============================================================
import { terrainSolids, type Terrain } from '../battlefield/presets';
import { fitCamera, project, type Viewport } from '../battlefield/projection';
import { buildScene } from '../battlefield/scene';
import { boundaryOutline, makeBoundary } from '../battlefield/boundary';
import { faceFill, faceOpacity } from '../battlefield/faceColour';
import type { PathGroup } from '../plate/figureArt';
import type { PlateFigure } from '../plate/figureFor';

/** A card's picture: 264 x 156, twice the card's CSS size for a sharp image. */
export const THUMB_VIEW: Viewport = { width: 264, height: 156 };

interface ThumbPolygon {
  points: { x: number; y: number }[];
  fill: string;
  opacity: number;
}

export interface ThumbShot {
  /** The map's floor, drawn first. */
  ground: { x: number; y: number }[];
  /** The faces, far to near: draw in order. */
  faces: ThumbPolygon[];
  /** The people placed on a scene, drawn over the faces. */
  dots?: { x: number; y: number }[];
  /** A character's figure: its art is loaded and fitted (`figureLines`) before the picture is drawn. */
  figure?: PlateFigure;
  /** The figure's line art fitted to the card: SVG path data under one canvas matrix. */
  lines?: FigureLines;
}

export interface FigureLines {
  paths: string[];
  /** The canvas transform [a, b, c, d, e, f] that puts the art's own frame onto the card. */
  matrix: [number, number, number, number, number, number];
}

/**
 * The establishing shot of a map: its ground and every solid's visible faces, hidden ones included (the GM's view).
 * `people` are a scene's placed tokens, on the ground plane (metres).
 */
export function thumbScene(
  terrain: Terrain,
  view: Viewport = THUMB_VIEW,
  people: readonly { x: number; z: number }[] = [],
): ThumbShot {
  const boundary = terrain.boundary ?? makeBoundary();
  const camera = fitCamera(boundary, view);
  const ground = boundaryOutline(boundary).map((p) => {
    const s = project(p, camera, view);
    return { x: s.x, y: s.y };
  });
  const faces = buildScene(terrainSolids(terrain), camera, view, { includeHidden: true }).map((f) => ({
    points: f.points.map((p) => ({ x: p.x, y: p.y })),
    fill: faceFill(f),
    opacity: faceOpacity(f) ?? 1,
  }));
  if (!people.length) return { ground, faces };
  const dots = people.map((p) => {
    const at = project({ x: p.x, y: 0, z: p.z }, camera, view);
    return { x: at.x, y: at.y };
  });
  return { ground, faces, dots };
}

/** A palette's picture: up to six of its colours as swatches on an empty floor. */
export function swatchShot(colours: readonly string[], view: Viewport = THUMB_VIEW): ThumbShot {
  const shown = colours.slice(0, 6);
  const w = view.width / 9;
  const left = (view.width - shown.length * w * 1.25) / 2;
  const faces = shown.map((fill, i) => {
    const x = left + i * w * 1.25;
    const [top, bottom] = [view.height * 0.3, view.height * 0.7];
    return {
      points: [
        { x, y: top },
        { x: x + w, y: top },
        { x: x + w, y: bottom },
        { x, y: bottom },
      ],
      fill,
      opacity: 1,
    };
  });
  return { ground: [], faces };
}

/** The plate art's frame, as PlateThumb's viewBox: the 2123 x 3000 figure with headroom for the head. */
const FIGURE_FRAME = { x: 0, y: -60, width: 2123, height: 3120 };

/** "translate(a,b) scale(c,d)" - the one form the traced art's groups use - as [c, 0, 0, d, a, b]. */
function artMatrix(transform: string): [number, number, number, number, number, number] {
  const num = (re: RegExp, fallback: number[]) => re.exec(transform)?.slice(1).map(Number) ?? fallback;
  const [a, b] = num(/translate\(\s*(-?[\d.]+)[ ,]+(-?[\d.]+)\s*\)/, [0, 0]);
  const [c, d] = num(/scale\(\s*(-?[\d.]+)(?:[ ,]+(-?[\d.]+))?\s*\)/, [1, 1]);
  return [c, 0, 0, Number.isFinite(d) ? d : c, a, b];
}

/** A figure's art fitted to the card, centred, as large as its frame allows. */
export function figureLines(art: PathGroup, view: Viewport = THUMB_VIEW): FigureLines {
  const s = Math.min(view.width / FIGURE_FRAME.width, view.height / FIGURE_FRAME.height);
  const ox = (view.width - s * FIGURE_FRAME.width) / 2 - s * FIGURE_FRAME.x;
  const oy = (view.height - s * FIGURE_FRAME.height) / 2 - s * FIGURE_FRAME.y;
  const [c, , , d, a, b] = artMatrix(art.transform);
  return { paths: art.paths, matrix: [s * c, 0, 0, s * d, s * a + ox, s * b + oy] };
}
